import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  addStandaloneOndertapijt,
  addUpsell,
  getUpsellEligibility,
  type UpsellEligibility,
} from '../../lib/upsell';
import { formatCurrency } from '../../lib/format';

interface Props {
  orderId: string;
  onClose: () => void;
  onAdded: () => void;
  /** Ververst de order zonder de dialog te sluiten (gebruikt bij gedeeltelijk gelukte batch). */
  onRefresh?: () => void;
}

interface StandaloneRow {
  id: number;
  lengteCm: number;
  breedteCm: number;
}

const UNAVAILABLE_ROLL_WIDTHS = new Set<number>();

function berekenRolbreedte(breedte: number, lengte: number): number | null {
  const shortest = Math.min(breedte, lengte);
  if (!Number.isFinite(shortest)) return null;
  const underlay = shortest - 10;
  const rounded = Math.ceil(underlay / 10) * 10;
  if (rounded < 60) return 60;
  if (rounded > 240) return 240;
  return rounded;
}

export function UpsellOndertapijtDialog({ orderId, onClose, onAdded, onRefresh }: Props) {
  const [eligibility, setEligibility] = useState<UpsellEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ondertapijt is standaard een upsell met 30% korting — geen coulance-korting.
  const [applyDiscount, setApplyDiscount] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  const [lengteCm, setLengteCm] = useState<string>('');
  const [breedteCm, setBreedteCm] = useState<string>('');
  const [standaloneRows, setStandaloneRows] = useState<StandaloneRow[]>([]);
  const [submittingStandalone, setSubmittingStandalone] = useState(false);
  const rowIdRef = useRef(0);
  const errorRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUpsellEligibility(orderId, 'ondertapijt', 0)
      .then((data) => {
        if (cancelled) return;
        setEligibility(data);
        const allEligible = new Set(
          data.items.filter((i) => !i.unavailable).map((i) => i.id)
        );
        setSelectedIds(allEligible);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const items = eligibility?.items ?? [];
  /** Kleden waar nog daadwerkelijk een ondertapijt bij kan. */
  const selecteerbaar = useMemo(() => items.filter((i) => !i.unavailable), [items]);
  /** Kleden waarvan de maat nog niet vastligt: bestellen mag, snijden nog niet. */
  const wachtOpNameten = useMemo(() => selecteerbaar.filter((i) => i.wachtOpNameten), [selecteerbaar]);

  const total = useMemo(() => {
    let sum = 0;
    for (const item of items) {
      if (!selectedIds.has(item.id) || item.unavailable) continue;
      sum += applyDiscount ? item.priceInclVat * 0.7 : item.priceInclVat;
    }
    return sum;
  }, [items, selectedIds, applyDiscount]);

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const lengteNum = Number(lengteCm);
  const breedteNum = Number(breedteCm);
  const lengteValid = Number.isFinite(lengteNum) && lengteNum >= 30 && lengteNum <= 800;
  const breedteValid = Number.isFinite(breedteNum) && breedteNum >= 30 && breedteNum <= 800;
  const standaloneValid = lengteValid && breedteValid;

  const standaloneRollWidth = standaloneValid ? berekenRolbreedte(breedteNum, lengteNum) : null;
  const standaloneRollUnavailable =
    standaloneRollWidth != null && UNAVAILABLE_ROLL_WIDTHS.has(standaloneRollWidth);
  const currentInputAddable = standaloneValid && !standaloneRollUnavailable;

  const pricePerM2 = eligibility?.pricePerM2InclVat ?? 9.92;
  const rowM2 = (row: StandaloneRow) => (row.lengteCm * row.breedteCm) / 10000;
  const rowPrice = (row: StandaloneRow) => {
    const raw = rowM2(row) * pricePerM2;
    return applyDiscount ? raw * 0.7 : raw;
  };

  const standalonePrice = currentInputAddable
    ? (() => {
        const raw = ((lengteNum * breedteNum) / 10000) * pricePerM2;
        return applyDiscount ? raw * 0.7 : raw;
      })()
    : 0;

  const standaloneRowsTotal = useMemo(
    () => standaloneRows.reduce((sum, row) => sum + rowPrice(row), 0),
    [standaloneRows, pricePerM2, applyDiscount]
  );

  // Aantal items dat "Toevoegen" zou indienen: de lijst + een nog niet toegevoegde,
  // geldige invoer (zodat één maat niet eerst "aan lijst" hoeft).
  const submitCount = standaloneRows.length + (currentInputAddable ? 1 : 0);

  const addStandaloneRow = () => {
    if (!currentInputAddable) return;
    setStandaloneRows((prev) => [
      ...prev,
      { id: rowIdRef.current++, lengteCm: lengteNum, breedteCm: breedteNum },
    ]);
    setLengteCm('');
    setBreedteCm('');
  };

  const removeStandaloneRow = (id: number) => {
    setStandaloneRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleSubmitStandalone = async () => {
    // Neem een nog niet toegevoegde, geldige invoer automatisch mee.
    const rows = currentInputAddable
      ? [...standaloneRows, { id: rowIdRef.current++, lengteCm: lengteNum, breedteCm: breedteNum }]
      : standaloneRows;
    if (rows.length === 0) return;

    setSubmittingStandalone(true);
    setError(null);
    let added = 0;
    for (const row of rows) {
      let result;
      try {
        result = await addStandaloneOndertapijt({
          workOrderId: orderId,
          lengteCm: row.lengteCm,
          breedteCm: row.breedteCm,
          discount: applyDiscount ? 30 : 0,
        });
      } catch (err) {
        result = {
          success: false,
          message: err instanceof Error ? err.message : 'toevoegen mislukt',
        };
      }
      if (!result.success) {
        setError(
          `${added} van ${rows.length} ondertapijten toegevoegd. Gestopt bij ${row.lengteCm} × ${row.breedteCm} cm: ${result.message || 'toevoegen mislukt'}`
        );
        // Houd de niet-ingediende rijen (incl. de mislukte) over voor correctie/herhaling.
        setStandaloneRows(rows.slice(added));
        setLengteCm('');
        setBreedteCm('');
        setSubmittingStandalone(false);
        if (added > 0) onRefresh?.();
        return;
      }
      added++;
    }
    onAdded();
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await addUpsell({
        workOrderId: orderId,
        type: 'ondertapijt',
        itemIds: Array.from(selectedIds),
        discount: applyDiscount ? 30 : 0,
      });
      if (!result.success) {
        setError(result.message || 'Upsell toevoegen mislukt');
        setSubmitting(false);
        return;
      }
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upsell toevoegen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Ondertapijt toevoegen</h2>
          {eligibility?.workOrderNumber && (
            <p className="text-xs text-slate-500 mt-1">
              {eligibility.workOrderNumber}
              {eligibility.customerName ? ` — ${eligibility.customerName}` : ''}
            </p>
          )}
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {loading && <p className="text-sm text-slate-500">Laden…</p>}
          {/*
            De order-brede vlag `Ondertapijt_besteld__c` is géén slot meer: een
            ondertapijt is een besluit per kleed, en de UpsellCard belooft met
            "+ Extra ondertapijt" al dat je kunt bijbestellen. Wat wél blokkeert
            staat per regel (`alreadyHasUnderlay`), zodat je ziet welk kleed er
            nog zonder ligt.
          */}
          {!loading && eligibility?.alreadyOrdered && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {selecteerbaar.length > 0
                ? `Er is al een ondertapijt besteld op deze order. ${selecteerbaar.length} kleed${selecteerbaar.length === 1 ? '' : 'en'} nog zonder — vink aan wat erbij moet.`
                : 'Elk kleed op deze order heeft al een ondertapijt.'}
            </p>
          )}
          {!loading && items.length === 0 && (
            <p className="text-sm text-slate-500">Geen items gevonden voor deze order.</p>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2">
              {items.map((item) => {
                const selected = selectedIds.has(item.id);
                const price = applyDiscount ? item.priceInclVat * 0.7 : item.priceInclVat;
                return (
                  <label
                    key={item.id}
                    className={clsx(
                      'flex items-center gap-3 px-3 py-2 border rounded cursor-pointer',
                      item.unavailable
                        ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                        : selected
                          ? 'border-indigo-300 bg-indigo-50/50'
                          : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected && !item.unavailable}
                      onChange={() => toggleItem(item.id)}
                      disabled={item.unavailable}
                      className="h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{item.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        {item.m2 != null && <span>{item.m2.toFixed(2)} m²</span>}
                        {item.tweedelig && <span className="text-amber-600">2-delig</span>}
                        {item.wachtOpNameten && !item.unavailable && (
                          <span className="text-amber-600">nog niet nagemeten</span>
                        )}
                        {item.unavailable && (
                          <span className="text-rose-600">{item.unavailableReason || 'Niet leverbaar'}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-slate-900">{formatCurrency(price)}</div>
                  </label>
                );
              })}
            </div>
          )}

          {!loading && wachtOpNameten.length > 0 && (
            <p className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              {wachtOpNameten.length === 1 ? 'Dit kleed is' : `${wachtOpNameten.length} kleden zijn`} nog
              niet nagemeten. Bestellen kan al: het ondertapijt komt pas ná de voorinspectie in de
              kniplijst en neemt dan de nagemeten maat en prijs over.
            </p>
          )}

          {!loading && selecteerbaar.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Totaal</span>
                <span className="font-medium text-slate-900">{formatCurrency(total)}</span>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-500">
                <input
                  type="checkbox"
                  checked={applyDiscount}
                  onChange={(e) => setApplyDiscount(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                30% ondertapijt-korting (standaard bij upsell)
              </label>
            </div>
          )}

          {!loading && (
            <details
              open={standaloneOpen}
              onToggle={(e) => setStandaloneOpen((e.target as HTMLDetailsElement).open)}
              className="mt-4 pt-3 border-t border-slate-100"
            >
              <summary className="text-xs font-medium text-slate-700 cursor-pointer hover:text-slate-900">
                + Ondertapijt voor extern kleed (niet in behandeling)
              </summary>
              <div className="mt-3 space-y-3">
                <p className="text-xs text-slate-500">
                  Voor kleden die we niet wassen — geef de afmetingen door, wij leveren de ondertapijten mee in dezelfde route. Voeg er meerdere toe voor verschillende kleden.
                </p>

                {standaloneRows.length > 0 && (
                  <ul className="space-y-1.5">
                    {standaloneRows.map((row, idx) => (
                      <li
                        key={row.id}
                        className="flex items-center justify-between gap-2 px-2.5 py-1.5 border border-slate-200 rounded text-sm"
                      >
                        <span className="text-slate-700">
                          {idx + 1}. {row.lengteCm} × {row.breedteCm} cm
                          <span className="text-slate-400"> · {rowM2(row).toFixed(2)} m²</span>
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-slate-900">{formatCurrency(rowPrice(row))}</span>
                          <button
                            type="button"
                            onClick={() => removeStandaloneRow(row.id)}
                            disabled={submittingStandalone}
                            aria-label={`Ondertapijt ${idx + 1} verwijderen`}
                            className="text-slate-400 hover:text-rose-600 disabled:opacity-50 leading-none text-base"
                          >
                            ×
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs text-slate-600">Lengte (cm)</span>
                    <input
                      type="number"
                      min={30}
                      max={800}
                      step={1}
                      value={lengteCm}
                      onChange={(e) => setLengteCm(e.target.value)}
                      className="mt-1 w-full px-2 py-1 text-sm border border-slate-300 rounded"
                      placeholder="200"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-600">Breedte (cm)</span>
                    <input
                      type="number"
                      min={30}
                      max={800}
                      step={1}
                      value={breedteCm}
                      onChange={(e) => setBreedteCm(e.target.value)}
                      className="mt-1 w-full px-2 py-1 text-sm border border-slate-300 rounded"
                      placeholder="150"
                    />
                  </label>
                </div>
                {standaloneValid && standaloneRollUnavailable && (
                  <p className="text-xs text-rose-600">
                    Deze afmeting valt op een tijdelijk niet-leverbare rolbreedte ({standaloneRollWidth} cm).
                  </p>
                )}
                {currentInputAddable && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      {((lengteNum * breedteNum) / 10000).toFixed(2)} m²
                    </span>
                    <span className="font-medium text-slate-900">{formatCurrency(standalonePrice)}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={addStandaloneRow}
                  disabled={!currentInputAddable || submittingStandalone}
                  className={clsx(
                    'w-full text-xs font-medium px-3 py-1.5 rounded border',
                    !currentInputAddable || submittingStandalone
                      ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'
                  )}
                >
                  + Toevoegen aan lijst
                </button>

                {standaloneRows.length > 0 && (
                  <div className="flex justify-between text-sm font-medium pt-2 border-t border-slate-100">
                    <span className="text-slate-500">Subtotaal ({standaloneRows.length})</span>
                    <span className="text-slate-900">{formatCurrency(standaloneRowsTotal)}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSubmitStandalone}
                  disabled={submittingStandalone || submitCount === 0}
                  className={clsx(
                    'w-full text-sm font-medium px-3 py-1.5 rounded',
                    submittingStandalone || submitCount === 0
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  )}
                >
                  {submittingStandalone
                    ? 'Toevoegen…'
                    : submitCount > 1
                      ? `${submitCount} ondertapijten toevoegen`
                      : 'Ondertapijt toevoegen'}
                </button>
              </div>
            </details>
          )}

          {error && <p ref={errorRef} className="mt-3 text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loading || selectedIds.size === 0}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || loading || selectedIds.size === 0
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Toevoegen…' : 'Bevestigen'}
          </button>
        </div>
      </div>
    </div>
  );
}
